import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import BarChartOutlined from '@mui/icons-material/BarChartOutlined'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'

import { getActionbyAudit } from '../../redux/actions/actionActions'
import { getAuditbyId } from '../../redux/audits/auditActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { formatTitle } from '../../utils/formatTitle'
import { writeEntityNav } from '../../utils/entityNav'
import UseAlert from '../../components/common/alert'
import {
  DetailAccordion,
  DetailButton,
  DetailInfoGrid,
  DetailInfoItem,
  DetailPageHead,
  RelatedActionsPanel,
} from '../../components/common/detail'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import '../../components/common/ListPageTable.css'

export default function ActionListByAudit() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)

  const actionsList = useSelector(
    (state: RootState) => state.action.actionsbyAudit
  )
  const audit = useSelector((state: RootState) => state.audit.audit)
  const loading = useSelector((state: RootState) => state.action.loading)

  const [open, setOpen] = useState({ info: true, actions: true })
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  useEffect(() => {
    if (id) {
      dispatch(getAuditbyId(id))
      dispatch(getActionbyAudit(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (!audit?._id) return
    writeEntityNav({
      auditId: audit._id,
      auditLabel: formatTitle(audit.AuditName || ''),
      missionId: audit.MissionId || undefined,
      missionLabel: audit.MissionName
        ? formatTitle(audit.MissionName)
        : undefined,
    })
  }, [audit])

  const canUpdate =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)
  const canAnalyze =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.DSSI) ||
    hasRole(roles, UserRole.ADMIN)

  const title = formatTitle(audit?.AuditName || '') || t('Audit')
  const rows = Array.isArray(actionsList) ? actionsList : []

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />

      <div className="detail-page">
        <DetailPageHead
          eyebrow={t('auditDetailsPage.eyebrow', {
            defaultValue: 'Management · Audits',
          })}
          title={title}
          subtitle={t('auditDetailsPage.subtitle', {
            defaultValue:
              'Audit information and corrective actions linked to this engagement.',
          })}
          actions={
            <>
              {canAnalyze ? (
                <DetailButton
                  variant="ghost"
                  onClick={() => navigate(`/audit/view-analysis/${id}`)}
                >
                  <BarChartOutlined sx={{ fontSize: 16 }} />
                  {t('buttons.viewAnalysis')}
                </DetailButton>
              ) : null}
              {canUpdate ? (
                <DetailButton
                  onClick={() => navigate(`/audit/${audit?._id}`)}
                >
                  <DriveFileRenameOutlineOutlined sx={{ fontSize: 16 }} />
                  {t('Update Audit', { defaultValue: 'Update Audit' })}
                </DetailButton>
              ) : null}
            </>
          }
        />

        <DetailAccordion
          title={t('Information', { defaultValue: 'Information' })}
          open={open.info}
          onToggle={() => setOpen((p) => ({ ...p, info: !p.info }))}
        >
          <DetailInfoGrid>
            <DetailInfoItem label={t('Name', { defaultValue: 'Name' })} value={title} />
            <DetailInfoItem
              label={t('Description', { defaultValue: 'Description' })}
              value={<ListPageDescription value={audit?.Description} />}
            />
            <DetailInfoItem
              label={t('Mission', { defaultValue: 'Mission' })}
              value={
                audit?.MissionName
                  ? formatTitle(audit.MissionName)
                  : '—'
              }
            />
          </DetailInfoGrid>
        </DetailAccordion>

        <RelatedActionsPanel
          title={t('Tasks', { defaultValue: 'Corrective actions' })}
          listTitle={t('tasks_list', { defaultValue: 'Task list' })}
          emptyMessage={t('auditDetailsPage.empty.actions', {
            defaultValue:
              'No corrective action is linked to this audit yet.',
          })}
          rows={rows}
          loading={loading}
          loadingDeps={[id]}
          roles={roles}
          open={open.actions}
          onToggle={() => setOpen((p) => ({ ...p, actions: !p.actions }))}
        />
      </div>
    </>
  )
}
